import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Repeat, X, ChevronRight, Loader2, Trash2 } from 'lucide-react';
import {
  getBookingSeries,
  frequencyLabel,
  deleteSeriesVisit,
  rescheduleSeriesFromVisit,
  SERIES_FREQUENCIES,
  type BookingSeries,
  type BookingSeriesVisit,
  type SeriesFrequency,
} from '../../lib/bookingSeries';

type SeriesVisit = BookingSeriesVisit;

interface BookingSeriesLineProps {
  seriesId: string;
  currentBookingId: string;
  isMobile?: boolean;
  /** Reload the open booking after a visit in the series changes. */
  onRefresh?: () => void | Promise<void>;
  /** Close the booking when the visit currently open is deleted. */
  onCurrentVisitDeleted?: () => void;
}

const fmtDate = (ymd: string) =>
  ymd ? new Date(`${ymd}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const fmtTime = (hm: string) => {
  if (!hm) return '';
  const [h, m] = hm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${ampm}`;
};

/** Same block color logic the calendar (and unscheduled tab) uses. */
const blockColorFor = (visit: SeriesVisit): { color: string; darkText: boolean } => {
  const status = visit.status;
  if (status === 'cancelled') return { color: '#fee2e2', darkText: true };
  if (status === 'in_progress') return { color: '#eab308', darkText: false };
  if (status === 'pending') return { color: '#a855f7', darkText: false };
  if (status === 'completed') return { color: '#22c55e', darkText: false };
  if (status === 'scheduled' && visit.color) return { color: visit.color, darkText: false };
  return { color: '#3b82f6', darkText: false };
};

/**
 * Recurring booking indicator — a slim line above the schedule showing this
 * visit's place in its series. Tapping it opens the series management modal
 * (all visits + Cancel All).
 */
export default function BookingSeriesLine({
  seriesId,
  currentBookingId,
  isMobile = false,
  onRefresh,
  onCurrentVisitDeleted,
}: BookingSeriesLineProps) {
  const [series, setSeries] = useState<BookingSeries | null>(null);
  const [visits, setVisits] = useState<SeriesVisit[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await getBookingSeries(seriesId);
      if (data) {
        setSeries(data.series);
        setVisits(data.bookings);
      }
    } catch (err) {
      console.error('Failed to load booking series:', err);
    }
  }, [seriesId]);

  useEffect(() => { load(); }, [load]);

  if (!series) return null;

  const cancelled = series.status !== 'active';
  const visitIndex = visits.findIndex(b => b.id === currentBookingId);

  return (
    <>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        className={`w-full flex items-center gap-2 bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] text-left hover:bg-gray-50 active:scale-[0.99] transition-all duration-150 touch-manipulation ${isMobile ? 'px-3 py-2' : 'px-4 py-2.5'}`}
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <Repeat className={`h-4 w-4 flex-shrink-0 ${cancelled ? 'text-gray-400' : 'text-blue-600'}`} />
        <span className="text-[13px] font-semibold text-gray-900 flex-shrink-0">Recurring booking</span>
        <span className="text-[12px] text-gray-500 truncate">
          {frequencyLabel(series.frequency)}
          {visitIndex >= 0 ? ` · Visit ${visitIndex + 1} of ${series.total_visits}` : ''}
          {cancelled ? ' · Series cancelled' : ''}
        </span>
        <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0 ml-auto" />
      </button>

      {modalOpen && (
        <BookingSeriesModal
          series={series}
          visits={visits}
          currentBookingId={currentBookingId}
          onClose={() => setModalOpen(false)}
          onChanged={load}
          onRefresh={onRefresh}
          onCurrentVisitDeleted={onCurrentVisitDeleted}
        />
      )}
    </>
  );
}

// ── Management modal — visit list + Cancel All ───────────────────────────────

function BookingSeriesModal({
  series,
  visits,
  currentBookingId,
  onClose,
  onChanged,
  onRefresh,
  onCurrentVisitDeleted,
}: {
  series: BookingSeries;
  visits: SeriesVisit[];
  currentBookingId: string;
  onClose: () => void;
  onChanged: () => Promise<void> | void;
  onRefresh?: () => void | Promise<void>;
  onCurrentVisitDeleted?: () => void;
}) {
  const [deletingAll, setDeletingAll] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [frequency, setFrequency] = useState<SeriesFrequency>(series.frequency);
  const [startDate, setStartDate] = useState('');
  const [applying, setApplying] = useState(false);

  const selected = visits.find((visit) => visit.id === selectedId) || null;

  const selectVisit = (visit: SeriesVisit) => {
    setSelectedId(visit.id);
    setFrequency(series.frequency);
    setStartDate(visit.start_date.slice(0, 10));
  };

  const handleDeleteAll = async () => {
    if (!confirm(`Delete ALL ${visits.length} bookings in this series? Each one is deleted exactly like deleting a booking normally (removed from the calendar, scheduling freed, reminders cancelled). This cannot be undone.`)) {
      return;
    }
    try {
      setDeletingAll(true);
      for (const visit of visits) {
        await deleteSeriesVisit(series.id, series.business_id, visit.id);
      }
      await onChanged();
      onClose();
      if (visits.some((visit) => visit.id === currentBookingId)) {
        onCurrentVisitDeleted?.();
      } else {
        await onRefresh?.();
      }
    } catch (err) {
      console.error('Failed to delete booking series:', err);
      alert('Failed to delete all bookings — some may already be deleted. Please check the calendar and try again.');
    } finally {
      setDeletingAll(false);
    }
  };

  const handleDeleteOne = async (visit: SeriesVisit) => {
    const when = fmtDate(visit.start_date);
    if (!confirm(`Delete the ${when} visit? The other visits in this series stay.`)) return;
    try {
      setDeletingId(visit.id);
      await deleteSeriesVisit(series.id, series.business_id, visit.id);
      if (selectedId === visit.id) setSelectedId(null);
      await onChanged();
      if (visit.id === currentBookingId) {
        onClose();
        onCurrentVisitDeleted?.();
      } else {
        await onRefresh?.();
      }
    } catch (err) {
      console.error('Failed to delete series visit:', err);
      alert('Failed to delete that visit. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleReschedule = async () => {
    if (!selected || !startDate) return;
    const when = fmtDate(startDate);
    if (!confirm(`Keep this visit on ${when} and every visit before it. Later visits that haven’t been completed will be removed and replaced with ${frequencyLabel(frequency).toLowerCase()} from ${when}.`)) {
      return;
    }
    try {
      setApplying(true);
      await rescheduleSeriesFromVisit({
        series,
        visits,
        anchorBookingId: selected.id,
        frequency,
        startDate,
      });
      await onChanged();
      await onRefresh?.();
      setSelectedId(null);
    } catch (err) {
      console.error('Failed to update series schedule:', err);
      alert(err instanceof Error ? err.message : 'Failed to update the schedule.');
    } finally {
      setApplying(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const scheduleUnchanged = !!selected
    && frequency === series.frequency
    && startDate === selected.start_date.slice(0, 10);
  const busy = deletingAll || applying || deletingId !== null;

  useEffect(() => {
    if (!selectedId) return;
    document.getElementById(`series-visit-${selectedId}`)?.scrollIntoView({ block: 'nearest' });
  }, [selectedId]);

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
      style={{ zIndex: 100200, backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}
      >
        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-200 px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-[17px] font-bold text-gray-900 flex items-center gap-2" style={{ letterSpacing: '-0.02em' }}>
              <Repeat className="h-4 w-4 text-blue-600" />
              Recurring Booking
            </h3>
            <p className="text-[12px] text-gray-400 mt-0.5">
              {frequencyLabel(series.frequency)} · {series.total_visits} visits
              {series.status !== 'active' ? ' · Cancelled' : ''}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Visit list — calendar-style blocks, matching the unscheduled tab */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1.5 bg-gray-50/50">
          {visits.length === 0 && (
            <p className="py-8 text-center text-sm text-gray-400">No visits remain in this series.</p>
          )}
          {visits.map((visit, idx) => {
            const isCurrent = visit.id === currentBookingId;
            const isSelected = visit.id === selectedId;
            const isPast = visit.start_date < today;
            const { color: blockColor, darkText } = blockColorFor(visit);
            const customerName = visit.customer
              ? `${visit.customer.first_name || ''} ${visit.customer.last_name || ''}`.trim()
              : '';
            const shouldShowSecondaryColor =
              !!visit.secondary_color &&
              visit.status !== 'in_progress' &&
              visit.status !== 'completed' &&
              visit.status !== 'cancelled';

            return (
              <div
                key={visit.id}
                id={`series-visit-${visit.id}`}
                role="button"
                tabIndex={0}
                onClick={() => selectVisit(visit)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    selectVisit(visit);
                  }
                }}
                className={`rounded-md overflow-hidden relative p-1 text-left cursor-pointer ${isSelected ? 'ring-2 ring-gray-900 ring-offset-1' : ''} ${isCurrent && !isSelected ? 'ring-2 ring-blue-400 ring-offset-1' : ''} ${isPast && !isCurrent ? 'opacity-70' : ''}`}
                style={{ backgroundColor: blockColor, color: darkText ? '#1f2937' : '#ffffff' }}
              >
                {shouldShowSecondaryColor && (
                  <div
                    className="absolute left-0 right-0"
                    style={{
                      backgroundColor: visit.secondary_color as string,
                      height: '20px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 2,
                      marginLeft: '-5px',
                      marginRight: '-5px',
                      boxShadow: '0 0 4px rgba(0,0,0,0.2)',
                      borderRadius: '2px',
                    }}
                  />
                )}

                {/* Line 1: booking number - customer + visit position */}
                <div className="flex items-center justify-between relative z-10 space-x-1">
                  <div className="flex items-center space-x-1 min-w-0">
                    {visit.booking_number != null && (
                      <div className="text-xs font-medium flex-shrink-0">{visit.booking_number}</div>
                    )}
                    {customerName && (
                      <div className="text-xs font-medium truncate">- {customerName}</div>
                    )}
                  </div>
                  <div className="text-[10px] font-semibold flex-shrink-0 opacity-90 flex items-center gap-1">
                    <span>Visit {idx + 1}{isCurrent ? ' · this visit' : ''}{isSelected ? ' · selected' : ''}</span>
                    <button
                      type="button"
                      title="Delete this visit"
                      disabled={busy}
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleDeleteOne(visit);
                      }}
                      className="rounded-full p-0.5 hover:bg-black/10 disabled:opacity-40"
                    >
                      {deletingId === visit.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Line 2: title */}
                <div className="text-sm truncate relative z-10 mt-0.5">
                  {visit.title || 'Untitled'}
                </div>

                {/* Line 3: date · time */}
                <div className="text-xs opacity-75 truncate relative z-10 mt-0.5">
                  {fmtDate(visit.start_date)} · {fmtTime(visit.start_time)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer — Delete All */}
        {series.status === 'active' && visits.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 flex-shrink-0 bg-white space-y-2">
            {selected && (
              <div className="space-y-2">
                <p className="text-[13px] font-semibold text-gray-900">New schedule from this visit</p>
                <p className="text-[11px] text-gray-500 leading-snug">
                  Keeps {fmtDate(startDate || selected.start_date)} and every visit before it. Later visits that haven’t been completed are replaced.
                </p>
                <select
                  value={frequency}
                  onChange={(event) => setFrequency(event.target.value as SeriesFrequency)}
                  disabled={busy}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-[13px] text-gray-900"
                >
                  {SERIES_FREQUENCIES.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <label className="block text-[12px] font-medium text-gray-700">
                  Start date
                  <input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    disabled={busy}
                    className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-[13px] text-gray-900"
                  />
                </label>
                <button
                  type="button"
                  disabled={busy || !startDate || scheduleUnchanged}
                  onClick={() => void handleReschedule()}
                  className="w-full py-2.5 rounded-xl bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 active:scale-[0.98] transition disabled:opacity-50 touch-manipulation"
                >
                  {applying ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Updating…
                    </span>
                  ) : (
                    'Update schedule'
                  )}
                </button>
              </div>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={handleDeleteAll}
              className="w-full py-2.5 rounded-xl bg-red-600 text-white text-[13px] font-semibold hover:bg-red-700 active:scale-[0.98] transition disabled:opacity-50 touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {deletingAll ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Deleting…
                </span>
              ) : (
                'Delete all bookings'
              )}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
